'use client';

import Icon from '@/components/ui/AppIcon';

interface BRSRLiteSectionProps {
  currentLanguage: string;
}

const BRSRLiteSection = ({ currentLanguage }: BRSRLiteSectionProps) => {
  const content = {
    en: {
      title: 'BRSR Lite Filing for Value Chain Partners',
      subtitle: 'Simplified sustainability reporting for NetZero alignment',
      badge: 'Value Chain Integration',
      intro: 'Streamlined BRSR Lite reporting system designed specifically for value chain partners committed to NetZero goals. Simplify compliance while demonstrating sustainability leadership.',
      benefits: [
        {
          icon: 'DocumentCheckIcon',
          title: 'Simplified Reporting',
          description: 'Streamlined forms with pre-filled templates and guided workflows'
        },
        {
          icon: 'ClockIcon',
          title: 'Time Efficient',
          description: 'Reduce reporting time by 60% with automated data collection'
        },
        {
          icon: 'ShieldCheckIcon',
          title: 'Compliance Assured',
          description: 'Built-in validation ensures regulatory compliance'
        },
        {
          icon: 'ChartBarIcon',
          title: 'Progress Tracking',
          description: 'Monitor your NetZero journey with real-time dashboards'
        }
      ],
      features: [
        {
          title: 'Automated Data Collection',
          items: ['Integration with existing systems', 'IoT sensor data sync', 'API connections', 'Manual data entry options']
        },
        {
          title: 'NetZero Alignment Tools',
          items: ['Carbon footprint calculator', 'Emission target setting', 'Progress milestone tracking', 'Gap analysis reports']
        },
        {
          title: 'Collaboration Features',
          items: ['Multi-user access', 'Role-based permissions', 'Comment threads', 'Version control']
        },
        {
          title: 'Reporting & Analytics',
          items: ['BRSR Lite format export', 'Custom report builder', 'Benchmark comparisons', 'Trend analysis']
        }
      ],
      cta: {
        title: 'Ready to Simplify Your BRSR Lite Reporting?',
        description: 'Join leading organizations in streamlined sustainability reporting',
        button: 'Start Free Trial'
      }
    },
    hi: {
      title: 'मूल्य श्रृंखला भागीदारों के लिए BRSR Lite फाइलिंग',
      subtitle: 'NetZero संरेखण के लिए सरलीकृत स्थिरता रिपोर्टिंग',
      badge: 'मूल्य श्रृंखला एकीकरण',
      intro: 'NetZero लक्ष्यों के प्रति प्रतिबद्ध मूल्य श्रृंखला भागीदारों के लिए विशेष रूप से डिज़ाइन किया गया सुव्यवस्थित BRSR Lite रिपोर्टिंग सिस्टम। स्थिरता नेतृत्व प्रदर्शित करते हुए अनुपालन को सरल बनाएं।',
      benefits: [
        {
          icon: 'DocumentCheckIcon',
          title: 'सरलीकृत रिपोर्टिंग',
          description: 'पूर्व-भरे टेम्पलेट और निर्देशित वर्कफ़्लो के साथ सुव्यवस्थित फॉर्म'
        },
        {
          icon: 'ClockIcon',
          title: 'समय कुशल',
          description: 'स्वचालित डेटा संग्रह के साथ रिपोर्टिंग समय में 60% की कमी'
        },
        {
          icon: 'ShieldCheckIcon',
          title: 'अनुपालन आश्वस्त',
          description: 'अंतर्निहित सत्यापन नियामक अनुपालन सुनिश्चित करता है'
        },
        {
          icon: 'ChartBarIcon',
          title: 'प्रगति ट्रैकिंग',
          description: 'वास्तविक समय डैशबोर्ड के साथ अपनी NetZero यात्रा की निगरानी करें'
        }
      ],
      features: [
        {
          title: 'स्वचालित डेटा संग्रह',
          items: ['मौजूदा सिस्टम के साथ एकीकरण', 'IoT सेंसर डेटा सिंक', 'API कनेक्शन', 'मैन्युअल डेटा प्रविष्टि विकल्प']
        },
        {
          title: 'NetZero संरेखण उपकरण',
          items: ['कार्बन फुटप्रिंट कैलकुलेटर', 'उत्सर्जन लक्ष्य सेटिंग', 'प्रगति मील का पत्थर ट्रैकिंग', 'अंतर विश्लेषण रिपोर्ट']
        },
        {
          title: 'सहयोग सुविधाएं',
          items: ['मल्टी-यूज़र एक्सेस', 'भूमिका-आधारित अनुमतियां', 'टिप्पणी थ्रेड', 'संस्करण नियंत्रण']
        },
        {
          title: 'रिपोर्टिंग और विश्लेषण',
          items: ['BRSR Lite प्रारूप निर्यात', 'कस्टम रिपोर्ट बिल्डर', 'बेंचमार्क तुलना', 'रुझान विश्लेषण']
        }
      ],
      cta: {
        title: 'अपनी BRSR Lite रिपोर्टिंग को सरल बनाने के लिए तैयार हैं?',
        description: 'सुव्यवस्थित स्थिरता रिपोर्टिंग में अग्रणी संगठनों में शामिल हों',
        button: 'मुफ्त परीक्षण शुरू करें'
      }
    },
    ar: {
      title: 'ملف BRSR Lite لشركاء سلسلة القيمة',
      subtitle: 'تقارير استدامة مبسطة لمواءمة NetZero',
      badge: 'تكامل سلسلة القيمة',
      intro: 'نظام تقارير BRSR Lite المبسط المصمم خصيصًا لشركاء سلسلة القيمة الملتزمين بأهداف NetZero. بسّط الامتثال أثناء إظهار قيادة الاستدامة.',
      benefits: [
        {
          icon: 'DocumentCheckIcon',
          title: 'تقارير مبسطة',
          description: 'نماذج مبسطة مع قوالب معبأة مسبقًا وسير عمل موجه'
        },
        {
          icon: 'ClockIcon',
          title: 'توفير الوقت',
          description: 'تقليل وقت إعداد التقارير بنسبة 60٪ مع جمع البيانات الآلي'
        },
        {
          icon: 'ShieldCheckIcon',
          title: 'ضمان الامتثال',
          description: 'التحقق المدمج يضمن الامتثال التنظيمي'
        },
        {
          icon: 'ChartBarIcon',
          title: 'تتبع التقدم',
          description: 'راقب رحلتك نحو NetZero بلوحات معلومات في الوقت الفعلي'
        }
      ],
      features: [
        {
          title: 'جمع البيانات الآلي',
          items: ['التكامل مع الأنظمة الحالية', 'مزامنة بيانات أجهزة استشعار IoT', 'اتصالات API', 'خيارات إدخال البيانات يدويًا']
        },
        {
          title: 'أدوات مواءمة NetZero',
          items: ['حاسبة البصمة الكربونية', 'تحديد أهداف الانبعاثات', 'تتبع مراحل التقدم', 'تقارير تحليل الفجوات']
        },
        {
          title: 'ميزات التعاون',
          items: ['وصول متعدد المستخدمين', 'أذونات قائمة على الدور', 'خيوط التعليقات', 'التحكم في الإصدار']
        },
        {
          title: 'التقارير والتحليلات',
          items: ['تصدير تنسيق BRSR Lite', 'منشئ تقارير مخصص', 'مقارنات المعايير', 'تحليل الاتجاهات']
        }
      ],
      cta: {
        title: 'هل أنت مستعد لتبسيط تقارير BRSR Lite الخاصة بك؟',
        description: 'انضم إلى المؤسسات الرائدة في التقارير المبسطة للاستدامة',
        button: 'ابدأ التجربة المجانية'
      }
    }
  };

  const currentContent = content[currentLanguage as keyof typeof content] || content.en;

  return (
    <section className="py-16 md:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-4 mb-12">
          <div className="inline-flex items-center space-x-2 bg-conversion/10 backdrop-blur-sm rounded-full px-6 py-2 border border-conversion/20">
            <Icon name="LinkIcon" size={20} className="text-conversion" />
            <span className="text-conversion font-semibold text-sm">
              {currentContent.badge}
            </span>
          </div>
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary">
            {currentContent.title}
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            {currentContent.subtitle}
          </p>
          <p className="text-base text-text-secondary max-w-3xl mx-auto leading-relaxed">
            {currentContent.intro}
          </p>
        </div>

        {/* Benefits */}
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6 mb-12">
          {currentContent.benefits.map((benefit, index) => (
            <div 
              key={index}
              className="bg-card border border-border rounded-xl p-6 text-center hover:border-conversion/50 hover:shadow-natural transition-all duration-300"
            >
              <div className="w-14 h-14 bg-conversion/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name={benefit.icon as any} size={28} className="text-conversion" />
              </div>
              <h3 className="font-semibold text-text-primary mb-2">{benefit.title}</h3>
              <p className="text-sm text-text-secondary">{benefit.description}</p>
            </div>
          ))}
        </div>

        {/* Features */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {currentContent.features.map((feature, index) => (
            <div 
              key={index}
              className="bg-card border border-border rounded-xl p-6"
            >
              <h3 className="font-heading font-bold text-xl text-text-primary mb-4">
                {feature.title}
              </h3>
              <ul className="space-y-3">
                {feature.items.map((item, iIndex) => (
                  <li key={iIndex} className="flex items-start space-x-3">
                    <Icon name="CheckCircleIcon" size={20} className="text-conversion mt-0.5 flex-shrink-0" />
                    <span className="text-text-secondary">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-conversion to-conversion/80 rounded-2xl p-8 md:p-12 text-center shadow-natural-lg">
          <h3 className="font-heading font-bold text-2xl md:text-3xl text-white mb-4">
            {currentContent.cta.title}
          </h3>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            {currentContent.cta.description}
          </p>
          <a
            href="#consultation"
            className="inline-flex items-center justify-center px-8 py-4 bg-white text-conversion font-heading font-semibold text-base rounded-lg hover:bg-gray-50 shadow-natural hover:shadow-natural-lg transition-all duration-300 ease-organic transform hover:scale-102"
          >
            {currentContent.cta.button}
            <Icon name="ArrowRightIcon" size={20} className="ml-2" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default BRSRLiteSection;